import { useCallback } from 'react';
import type { MouseEvent } from 'react';

import { getWindow } from '@change-corgi/core/window';
import { shouldNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionGuidesCategoryPageData } from '#src/app/pages/petitionGuides/category/details/pageContext';

type PageItem = {
	type: 'page';
	page: number;
	href: string;
	current: boolean;
};

type EllipsisItem = {
	type: 'ellipsis';
};

type PaginationItem = PageItem | EllipsisItem;

type AdjacentPage = {
	page: number;
	href: string;
} | null;

type Result = ModelHookResult<
	{
		totalPages: number;
		items: readonly PaginationItem[];
		previous: AdjacentPage;
		next: AdjacentPage;
	},
	{
		handlePageClick: (page: number, event: MouseEvent<HTMLAnchorElement>) => void;
	}
>;

const SIBLING_COUNT = 1;

function getPageHref(slug: string, page: number): string {
	return page === 1 ? `/petition-guides/category/${slug}` : `/petition-guides/category/${slug}?page=${page}`;
}

/**
 * Always keeps the first page, the last page, the current page and one sibling either side
 * of it, then fills the gaps left between those with a single ellipsis marker each.
 */
function getPageNumbers(currentPage: number, totalPages: number): ReadonlyArray<number | 'ellipsis'> {
	const mustShowPages = Array.from(
		new Set(
			[1, totalPages, currentPage - SIBLING_COUNT, currentPage, currentPage + SIBLING_COUNT].filter(
				(page) => page >= 1 && page <= totalPages,
			),
		),
	).sort((a, b) => a - b);

	return mustShowPages.flatMap((page, index) => {
		const previousShownPage = mustShowPages[index - 1];
		return index > 0 && page - previousShownPage > 1 ? (['ellipsis', page] as const) : [page];
	});
}

/**
 * Builds the prev/next/numbered pagination data, and on click tracks the destination page and
 * jumps to the top of the document.
 *
 * Focus is handled by `useArticleList` when the new results actually render, since the focus
 * target belongs to the list rather than to this control.
 */
export function useCategoryPagination(): Result {
	const {
		data: { slug, page: currentPage, totalPages },
	} = usePetitionGuidesCategoryPageData();
	const track = useTracking();

	const items: readonly PaginationItem[] = getPageNumbers(currentPage, totalPages).map((page) =>
		page === 'ellipsis'
			? { type: 'ellipsis' }
			: { type: 'page', page, href: getPageHref(slug, page), current: page === currentPage },
	);

	return {
		data: {
			totalPages,
			items,
			previous: currentPage > 1 ? { page: currentPage - 1, href: getPageHref(slug, currentPage - 1) } : null,
			next: currentPage < totalPages ? { page: currentPage + 1, href: getPageHref(slug, currentPage + 1) } : null,
		},
		actions: {
			handlePageClick: useCallback(
				(targetPage: number, event: MouseEvent<HTMLAnchorElement>) => {
					// A cmd/ctrl/shift-click opens the page in a new tab or window and leaves this one where
					// it is, so neither the scroll nor the event applies to it.
					if (!shouldNavigate(event)) return;

					getWindow().scrollTo({ top: 0, behavior: 'instant' });
					void track('petition_guides_category_page_pagination_click', {
						destination_page: targetPage,
						category_slug: slug,
					});
				},
				[track, slug],
			),
		},
	};
}
